import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import {
  ref,
  get,
  set,
  update,
  onValue,
} from "firebase/database";

import { auth } from "../services/firebase/auth";
import { rtdb } from "../services/firebase/rtdb";

const ADMIN_SESSION_KEY = "onurb_admin_session";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(
    () => localStorage.getItem(ADMIN_SESSION_KEY) === "1",
  );

  // ─── Firebase Auth listener ────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setProfile(null);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // ─── Real-time RTDB profile listener ──────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const userRef = ref(rtdb, `users/${user.uid}`);
    const unsub = onValue(
      userRef,
      (snap) => {
        setProfile(snap.exists() ? snap.val() : null);
        setLoading(false);
      },
      (err) => {
        console.warn("AuthContext: profile listener error:", err);
        setProfile(null);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [user?.uid]);

  // ─── Derived state ─────────────────────────────────────────────────────────
  const role = profile?.role ?? null;
  const approvalStatus = profile?.approvalStatus ?? null;
  const isAuthenticated = !!user;

  // ─── ADMIN LOGIN (localStorage, credenciais fixas para MVP) ───────────────
  const adminLogin = (login, password) => {
    if (login === "admin" && password === "admin") {
      localStorage.setItem(ADMIN_SESSION_KEY, "1");
      setIsAdminAuthenticated(true);
      return true;
    }
    return false;
  };

  const adminLogout = () => {
    localStorage.removeItem(ADMIN_SESSION_KEY);
    setIsAdminAuthenticated(false);
  };

  // ─── CUSTOMER REGISTER ─────────────────────────────────────────────────────
  const registerCustomer = async ({
    name,
    email,
    phone,
    cpf,
    birth,
    password,
    marketingOptIn,
  }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;

    if (name) await updateProfile(cred.user, { displayName: name });

    // RTDB: users/{uid}
    await set(ref(rtdb, `users/${uid}`), {
      uid,
      name: name || "",
      email: email || "",
      phone: phone || "",
      cpf: cpf || "",
      birth: birth || "",
      marketingOptIn: !!marketingOptIn,
      role: "customer",
      approvalStatus: null,
      active: true,
      createdAtMs: Date.now(),
      updatedAtMs: Date.now(),
    });

    return cred.user;
  };

  // ─── ORGANIZER REGISTER ────────────────────────────────────────────────────
  const registerOrganizer = async (formData, password) => {
    const email = formData.organizerEmail;
    const name = formData.organizerName;
    const phone = formData.organizerPhone;

    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;

    if (name) await updateProfile(cred.user, { displayName: name });

    // RTDB: users/{uid} — role organizer, pending
    await set(ref(rtdb, `users/${uid}`), {
      uid,
      name: name || "",
      email: email || "",
      phone: phone || "",
      role: "organizer",
      approvalStatus: "pending",
      active: false,
      createdAtMs: Date.now(),
      updatedAtMs: Date.now(),
    });

    // RTDB: organizerRequests/{uid}
    await set(ref(rtdb, `organizerRequests/${uid}`), {
      uid,
      status: "pending",
      organizer: {
        name: formData.organizerName || "",
        email: formData.organizerEmail || "",
        phone: formData.organizerPhone || "",
        cityState: formData.organizerCityState || "",
        type: formData.organizerType || "",
      },
      event: {
        name: formData.eventName || "",
        type: formData.eventType || "",
        cityState: formData.eventCityState || "",
        date: formData.eventDate || "",
        frequency: formData.eventFrequency || "",
      },
      finance: {
        alreadySells: formData.alreadySells || "",
        currentPlatform: formData.currentPlatform || "",
        audienceRange: formData.audienceRange || "",
        priceRange: formData.priceRange || "",
        wantsAdvance: formData.wantsAdvance || "",
      },
      acceptedTerms: !!formData.acceptTerms,
      createdAtMs: Date.now(),
      updatedAtMs: Date.now(),
    });

    return { user: cred.user, uid };
  };

  // ─── SIGN IN ───────────────────────────────────────────────────────────────
  const signIn = async (email, password, opts = {}) => {
    const remember = opts?.remember ?? true;
    await setPersistence(
      auth,
      remember ? browserLocalPersistence : browserSessionPersistence,
    );
    const cred = await signInWithEmailAndPassword(auth, email, password);

    // Lê o profile imediatamente (para redirect no login)
    const snap = await get(ref(rtdb, `users/${cred.user.uid}`));
    const profileData = snap.exists() ? snap.val() : null;

    return { user: cred.user, profile: profileData };
  };

  // ─── LOGOUT ────────────────────────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setProfile(null);
  };

  // ─── RESET SENHA ───────────────────────────────────────────────────────────
  const sendPasswordReset = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // ─── ATUALIZAR PERFIL ──────────────────────────────────────────────────────
  const updateProfileData = async (data) => {
    if (!user) throw new Error("Usuário não autenticado");
    await update(ref(rtdb, `users/${user.uid}`), {
      ...data,
      updatedAtMs: Date.now(),
    });
  };

  // ─── signUp como alias de registerCustomer (backward compat) ──────────────
  const signUp = registerCustomer;

  // ─── Context value ─────────────────────────────────────────────────────────
  const value = useMemo(
    () => ({
      // estado
      user,
      profile,
      loading,
      role,
      approvalStatus,
      isAuthenticated,
      isAdminAuthenticated,
      // auth de customer/organizer
      signIn,
      signUp,
      registerCustomer,
      registerOrganizer,
      logout,
      sendPasswordReset,
      updateProfileData,
      // auth admin
      adminLogin,
      adminLogout,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, profile, loading, isAdminAuthenticated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error("useAuthContext deve ser usado dentro de <AuthProvider />");
  return ctx;
}
